import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import {
  ChartRange,
  CompanyOverview,
  OrderSide,
  PriceChart,
  tradingServices,
  TradingAccount,
  TradingPosition,
} from '../../services/trading-service'
import { Skeleton } from '../../lib/components/Skeleton'
import { MarketStatusBadge, easternClock } from './market-status'
import { OrderTicket } from './order-ticket'
import { usd } from './order-rules'
import './trading.scss'

const RANGES: ChartRange[] = ['1D', '1W', '1M', '1Y']

const signed = (n: number, format: (v: number) => string) =>
  `${n > 0 ? '+' : n < 0 ? '−' : ''}${format(Math.abs(n))}`

const volume = (n: number | null) =>
  n === null ? '—' : n.toLocaleString('en-US', { notation: 'compact', maximumFractionDigits: 1 })

const tickFor = (range: ChartRange) => (t: string) => {
  const d = new Date(t)
  const opts: Intl.DateTimeFormatOptions =
    range === '1D'
      ? { hour: 'numeric', minute: '2-digit' }
      : range === '1Y'
      ? { month: 'short', year: '2-digit' }
      : { month: 'short', day: 'numeric' }
  return new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', ...opts }).format(d)
}

const PriceChartCard = ({ symbol }: { symbol: string }) => {
  const [range, setRange] = useState<ChartRange>('1D')
  const [chart, setChart] = useState<PriceChart | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    setChart(null)
    setFailed(false)
    const load = async () => {
      const res = await tradingServices.getChart(symbol, range)
      if (cancelled) return
      if (res?.status === 200) setChart(res.data)
      else setFailed(true)
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [symbol, range])

  const points = chart?.points ?? []
  const up = points.length > 1 && points[points.length - 1].c >= points[0].c
  const color = up ? '#0a7d3c' : '#b3261e'

  return (
    <section className='trading-page__card' data-testid='price-chart'>
      <div className='trading-page__card-head'>
        <h2>Price</h2>
        <div className='trading-page__segmented is-small' role='group' aria-label='Chart range'>
          {RANGES.map((r) => (
            <button
              key={r}
              type='button'
              aria-pressed={range === r}
              className={range === r ? 'is-active' : ''}
              onClick={() => setRange(r)}
            >
              {r}
            </button>
          ))}
        </div>
      </div>
      <div className='trading-page__chart'>
        {failed ? (
          <p className='trading-page__error'>The price chart is unavailable right now.</p>
        ) : !chart ? (
          <Skeleton width='100%' height='220px' />
        ) : points.length === 0 ? (
          <p className='trading-page__muted'>No price data for this range.</p>
        ) : (
          <ResponsiveContainer width='100%' height={220}>
            <AreaChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id='priceFill' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='0%' stopColor={color} stopOpacity={0.18} />
                  <stop offset='100%' stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey='t'
                tickFormatter={tickFor(range)}
                minTickGap={40}
                tick={{ fontSize: 12 }}
                stroke='#9aa0a6'
              />
              <YAxis
                domain={['auto', 'auto']}
                tickFormatter={(v: number) => `$${v.toFixed(0)}`}
                width={56}
                tick={{ fontSize: 12 }}
                stroke='#9aa0a6'
              />
              <Tooltip
                formatter={(v: number) => [usd(v), 'Price']}
                labelFormatter={(t: string) =>
                  new Intl.DateTimeFormat('en-US', {
                    timeZone: 'America/New_York',
                    dateStyle: 'medium',
                    timeStyle: range === '1D' || range === '1W' ? 'short' : undefined,
                  }).format(new Date(t))
                }
              />
              <Area
                type='monotone'
                dataKey='c'
                stroke={color}
                strokeWidth={2}
                fill='url(#priceFill)'
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
      {chart && chart.delayMinutes > 0 ? (
        <p className='trading-page__muted'>
          Chart ends {chart.delayMinutes} minutes behind the market.
        </p>
      ) : null}
    </section>
  )
}

/**
 * Company page: what Alpaca knows about a stock (exchange, trading flags,
 * delayed price, day range, volume, chart) and the investor's position,
 * with Buy / Sell opening the order ticket.
 */
const CompanyContainer = () => {
  const { symbol: param = '' } = useParams()
  const symbol = param.toUpperCase()
  const [company, setCompany] = useState<CompanyOverview | null>(null)
  const [status, setStatus] = useState<'loading' | 'ready' | 'notFound' | 'error'>('loading')
  const [account, setAccount] = useState<TradingAccount | null>(null)
  const [positions, setPositions] = useState<TradingPosition[]>([])
  const [side, setSide] = useState<OrderSide | null>(null)
  const ticketRef = useRef<HTMLDivElement>(null)

  const load = useCallback(async () => {
    const [overview, acc] = await Promise.all([
      tradingServices.getCompany(symbol),
      tradingServices.getAccount(),
    ])
    if (overview?.status === 200) {
      setCompany(overview.data)
      setStatus('ready')
    } else {
      setStatus(overview?.status === 404 ? 'notFound' : 'error')
    }
    const active = acc?.status === 200 && acc.data.status === 'ACTIVE'
    setAccount(acc?.status === 200 ? acc.data : null)
    if (active) {
      const pos = await tradingServices.getPositions()
      if (pos?.status === 200) setPositions(pos.data)
    }
  }, [symbol])

  useEffect(() => {
    setStatus('loading')
    setCompany(null)
    setSide(null)
    void load()
  }, [load])

  const open = (s: OrderSide) => {
    setSide(s)
    window.setTimeout(
      () => ticketRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }),
      0,
    )
  }

  const back = (
    <Link to='/dashboard/trading' className='trading-page__back'>
      ← Trade stocks
    </Link>
  )

  if (status === 'notFound' || status === 'error') {
    return (
      <div className='trading-page'>
        {back}
        <p className='trading-page__error' role='alert'>
          {status === 'notFound'
            ? `We couldn't find a stock with the symbol ${symbol}.`
            : 'This stock is unavailable right now. Please try again shortly.'}
        </p>
      </div>
    )
  }

  const q = company?.quote ?? null
  const canTrade = account?.status === 'ACTIVE' && company?.tradable
  const held = company?.position

  return (
    <div className='trading-page' aria-busy={status === 'loading'}>
      {back}
      <header className='trading-page__company'>
        <div>
          <h1>{company ? company.name : <Skeleton width='280px' height='28px' />}</h1>
          <p className='trading-page__muted'>
            {symbol}
            {company?.exchange ? ` · ${company.exchange}` : ''}
          </p>
          {company ? (
            <div className='trading-page__badges'>
              <b className={`trading-page__badge${company.tradable ? '' : ' is-off'}`}>
                {company.tradable ? 'Tradable' : 'Not tradable'}
              </b>
              {company.fractionable ? (
                <b className='trading-page__badge'>Fractional shares</b>
              ) : null}
              {company.shortable ? <b className='trading-page__badge'>Shortable</b> : null}
            </div>
          ) : null}
        </div>
        <div className='trading-page__price'>
          {!company ? (
            <Skeleton width='140px' height='32px' />
          ) : q?.price ? (
            <>
              <strong>{usd(q.price)}</strong>
              {q.change !== null && q.changePercent !== null ? (
                <span className={q.change < 0 ? 'is-loss' : 'is-gain'}>
                  {signed(q.change, (v) => usd(v))} (
                  {signed(q.changePercent, (v) => `${v.toFixed(2)}%`)}) today
                </span>
              ) : null}
            </>
          ) : (
            <span className='trading-page__muted'>No price available</span>
          )}
          {company ? <MarketStatusBadge market={company.market} /> : null}
          {company && company.delayMinutes > 0 && q?.asOf ? (
            <small className='trading-page__muted'>
              Delayed {company.delayMinutes} min · as of {easternClock(q.asOf)}
            </small>
          ) : null}
        </div>
      </header>

      <section className='trading-page__facts' data-testid='company-facts'>
        {(
          [
            ['Open', usd(q?.open)],
            ['Day range', q?.low && q?.high ? `${usd(q.low)} – ${usd(q.high)}` : '—'],
            ['Previous close', usd(q?.previousClose)],
            ['Volume', volume(q?.volume ?? null)],
            ['Bid', usd(q?.bid)],
            ['Ask', usd(q?.ask)],
          ] as Array<[string, string]>
        ).map(([label, value]) => (
          <div key={label}>
            <span>{label}</span>
            <strong>{company ? value : <Skeleton width='72px' />}</strong>
          </div>
        ))}
      </section>

      <PriceChartCard symbol={symbol} />

      {held ? (
        <section className='trading-page__card' data-testid='company-position'>
          <h2>Your position</h2>
          <div className='trading-page__facts is-plain'>
            <div>
              <span>Shares</span>
              <strong>{held.qty}</strong>
            </div>
            <div>
              <span>Average cost</span>
              <strong>{usd(held.avgEntryPrice)}</strong>
            </div>
            <div>
              <span>Market value</span>
              <strong>{usd(held.marketValue)}</strong>
            </div>
            <div>
              <span>Unrealized P/L</span>
              <strong className={held.unrealizedPl < 0 ? 'is-loss' : 'is-gain'}>
                {usd(held.unrealizedPl)}
              </strong>
            </div>
          </div>
        </section>
      ) : null}

      {company ? (
        canTrade ? (
          <div className='trading-page__actions'>
            <button type='button' className='trading-page__btn' onClick={() => open('buy')}>
              Buy {symbol}
            </button>
            <button
              type='button'
              className='trading-page__btn is-secondary'
              onClick={() => open('sell')}
              disabled={!held}
              title={held ? undefined : `You have no ${symbol} shares to sell`}
            >
              Sell {symbol}
            </button>
          </div>
        ) : (
          <p className='trading-page__card'>
            {company.tradable ? (
              <>
                To trade {symbol}, <Link to='/dashboard/trading'>open a brokerage account</Link> or
                wait until it is active.
              </>
            ) : (
              `${symbol} cannot be traded through Greenback right now.`
            )}
          </p>
        )
      ) : null}

      {side && company ? (
        <div ref={ticketRef}>
          <OrderTicket
            selected={{ symbol, name: company.name }}
            positions={positions}
            side={side}
            onPlaced={() => void load()}
          />
        </div>
      ) : null}

      <p className='trading-page__disclaimer'>
        Brokerage services are provided by Alpaca Securities LLC, member FINRA/SIPC. Investing
        involves risk, including loss of principal.
      </p>
    </div>
  )
}

export { CompanyContainer }
